import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../assets/styles/Contact.scss';
import emailjs from '@emailjs/browser';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';

// Styled component for TextField
const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    '& input': {
      color: 'black',
    },
    '& textarea': {
      color: 'black',
    },
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.87)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#5000ca',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(0, 0, 0, 0.7)',
    '&.Mui-focused': {
      color: '#5000ca',
    },
  },
  '& .MuiFormHelperText-root': {
    '&.Mui-error': {
      color: '#d32f2f',
    },
  },
});

function Contact() {
  const { t } = useTranslation();
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const [nameError, setNameError] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // const form = useRef();

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate fields
    const isNameEmpty = name.trim() === '';
    const isEmailEmpty = email.trim() === '';
    const isMessageEmpty = message.trim() === '';
    const isEmailValid = validateEmail(email);

    setNameError(isNameEmpty);
    setEmailError(isEmailEmpty || !isEmailValid);
    setMessageError(isMessageEmpty);

    if (!isNameEmpty && !isEmailEmpty && isEmailValid && !isMessageEmpty) {
      setLoading(true);
      
      const templateParams = {
        from_name: name,
        from_email: email,
        message: message,
      };

      try {
        const response = await emailjs.send(
          'service_pmu8wg9',  // Replace with your EmailJS service ID
          'template_4eyz0fe', // Replace with your EmailJS template ID
          templateParams,
          'kBLzX2_RYagsg8hKh'   // Replace with your EmailJS public key
        );

        if (response.status === 200) {
          setSnackbarMessage(t('contact.successMessage'));
          setSnackbarSeverity('success');
          setOpenSnackbar(true);
          
          // Clear form
          setName('');
          setEmail('');
          setMessage('');
        }
      } catch (error) {
        setSnackbarMessage(t('contact.errorMessage'));
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div id="contact">
      <div className="items-container">
        <div className="contact_wrapper">
          <h1>{t('contact.title')}</h1>
          <p>{t('contact.description')}</p>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            className='contact-form'
            onSubmit={sendEmail}
          >
            <div className='form-flex'>
              <StyledTextField
                required
                id="outlined-required"
                label={t('contact.name')}
                placeholder={t('contact.namePlaceholder')}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value !== '') setNameError(false);
                }}
                error={nameError}
                helperText={nameError ? t('contact.nameError') : ""}
                disabled={loading}
                variant="outlined"
              />
              <StyledTextField
                required
                id="outlined-required"
                label={t('contact.email')}
                type="email"
                placeholder={t('contact.emailPlaceholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (validateEmail(e.target.value)) setEmailError(false);
                }}
                error={emailError}
                helperText={emailError ? t('contact.emailError') : ""}
                disabled={loading}
                variant="outlined"
              />
            </div>
            <StyledTextField
              required
              id="outlined-multiline-static"
              label={t('contact.message')}
              placeholder={t('contact.messagePlaceholder')}
              multiline
              rows={10}
              className="body-form"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (e.target.value !== '') setMessageError(false);
              }}
              error={messageError}
              helperText={messageError ? t('contact.messageError') : ""}
              disabled={loading}
              variant="outlined"
            />
            <Button 
              variant="contained" 
              endIcon={<SendIcon />} 
              type="submit"
              disabled={loading}
            >
              {loading ? t('contact.sending') : t('contact.send')}
            </Button>
          </Box>
        </div>
      </div>
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Contact;